// Deleted Rentals Modal - จอ "รายการที่ลบ" แสดงรายการเช่าที่ถูก soft delete + ปุ่มกู้คืน
// คู่กับ backend GET /rentals/deleted/list + POST /rentals/{id}/restore
import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../ui/Modal';
import {
  RiDeleteBinLine,
  RiArrowGoBackLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiLoader4Line,
} from 'react-icons/ri';
import rentalService, { DeletedRental } from '../../services/rentalService';
import { showConfirm, showSuccess, showError } from '../../utils/alert';
import { formatDate } from '../../utils/dateUtils';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

export interface DeletedRentalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** เรียกหลังกู้คืนสำเร็จ เพื่อให้หน้ารายการเช่าหลัก refresh */
  onRestored?: () => void;
}

const PAGE_LIMIT = 10;

const DeletedRentalsModal: React.FC<DeletedRentalsModalProps> = ({ isOpen, onClose, onRestored }) => {
  const [items, setItems] = useState<DeletedRental[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const loadData = useCallback(async (targetPage: number) => {
    setLoading(true);
    try {
      const res = await rentalService.getDeletedRentals(targetPage, PAGE_LIMIT);
      setItems(res.items);
      setTotal(res.total);
      setTotalPages(Math.max(1, res.total_pages));
      setPage(res.page);
    } catch (err: any) {
      showError(getApiErrorMessage(err, 'ไม่สามารถโหลดรายการที่ลบได้'), 'โหลดข้อมูลไม่สำเร็จ'); // KD3 S2376: เดิม err.message = ข้อความ axios อังกฤษ
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadData(1);
    }
  }, [isOpen, loadData]);

  const handleRestore = async (rental: DeletedRental) => {
    const confirmed = await showConfirm(
      'กู้คืนรายการเช่า',
      `ต้องการกู้คืนรายการเช่า ${rental.rental_number} หรือไม่? รายการจะกลับมาแสดงในหน้ารายการเช่าตามสถานะเดิม`
    );
    if (!confirmed) return;

    setRestoringId(rental.id);
    try {
      await rentalService.restoreRental(rental.id);
      showSuccess(`กู้คืนรายการเช่า ${rental.rental_number} เรียบร้อยแล้ว`, 'กู้คืนสำเร็จ');
      // ถ้าหน้านี้เหลือรายการเดียวและไม่ใช่หน้าแรก ให้ถอยไปหน้าก่อน
      const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
      await loadData(nextPage);
      onRestored?.();
    } catch (err: any) {
      // 409 = อุปกรณ์ในรายการไม่ว่างแล้ว (ถูกปล่อยเช่าให้รายอื่นระหว่างที่ลบอยู่)
      showError(getApiErrorMessage(err, 'ไม่สามารถกู้คืนรายการเช่าได้'), 'กู้คืนไม่สำเร็จ');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <RiDeleteBinLine className="h-5 w-5 text-secondary-500" />
          รายการที่ลบ (กู้คืนได้)
        </span>
      }
      size="4xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-secondary-600">
          รายการเช่าที่ถูกลบจะถูกเก็บไว้ (Soft Delete) สามารถกู้คืนกลับมาใช้งานได้
          {total > 0 && <span className="ml-1 font-medium">ทั้งหมด {total} รายการ</span>}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-secondary-500">
            <RiLoader4Line className="h-6 w-6 animate-spin mr-2" />
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-secondary-500">
            <RiDeleteBinLine className="h-10 w-10 mx-auto mb-2 text-secondary-300" />
            ไม่มีรายการเช่าที่ถูกลบ
          </div>
        ) : (
          <div className="overflow-x-auto border border-secondary-200 rounded-lg">
            <table className="min-w-full divide-y divide-secondary-200 text-sm">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold text-secondary-700">รหัสการเช่า</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-secondary-700">บริษัท/โครงการ</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-secondary-700">สถานะ</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-secondary-700">วันที่ลบ</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-secondary-700">ลบโดย</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-secondary-700">การดำเนินการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100 bg-white">
                {items.map((r) => {
                  const statusDisplay = r.status ? getRentalStatusDisplay(r.status) : null;
                  return (
                    <tr key={r.id} className="hover:bg-secondary-50">
                      <td className="px-3 py-2.5 font-medium text-secondary-900 whitespace-nowrap">{r.rental_number}</td>
                      <td className="px-3 py-2.5 text-secondary-700">
                        <div className="font-medium">{r.company_name || '—'}</div>
                        {r.site_name && <div className="text-xs text-secondary-500">{r.site_name}</div>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {statusDisplay ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusDisplay.badgeColor}`}>
                            {statusDisplay.label}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-secondary-600 whitespace-nowrap">{r.deleted_at ? formatDate(r.deleted_at) : '—'}</td>
                      <td className="px-3 py-2.5 text-secondary-600 whitespace-nowrap">{r.deleted_by || '—'}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => handleRestore(r)}
                          disabled={restoringId !== null}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {restoringId === r.id ? (
                            <RiLoader4Line className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RiArrowGoBackLine className="h-3.5 w-3.5" />
                          )}
                          กู้คืน
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-secondary-600">
            <span>หน้า {page} จาก {totalPages}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => loadData(page - 1)}
                disabled={page <= 1 || loading}
                className="inline-flex items-center px-2.5 py-1.5 border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RiArrowLeftSLine className="h-4 w-4" /> ก่อนหน้า
              </button>
              <button
                type="button"
                onClick={() => loadData(page + 1)}
                disabled={page >= totalPages || loading}
                className="inline-flex items-center px-2.5 py-1.5 border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ถัดไป <RiArrowRightSLine className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DeletedRentalsModal;
